import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store';
import { selectDbData } from '../../../store/selectors/databaseSelectors';
import { addOptimizer } from '../../../store/slices/databaseSlice';
import { optimizersService } from '../../../services';
import { Optimizer } from '../../../types';
import { TOAST_CONFIG, TOAST_WARNING_CONFIG } from '../../../constants';
import toast from 'react-hot-toast';

interface OptimizerForm {
  type: string;
  current: string;
}

export const OptimizerManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const dbData = useAppSelector(selectDbData);
  
  // Form state
  const [optimizerForm, setOptimizerForm] = useState<OptimizerForm>({
    type: '',
    current: ''
  });
  
  // UI states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localOptimizers, setLocalOptimizers] = useState<Optimizer[]>([]);

  // Initialize local state with Redux data
  useEffect(() => {
    setLocalOptimizers(dbData.optimizers);
  }, [dbData.optimizers]);

  const handleOptimizerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!optimizerForm.type.trim()) {
      toast('Please enter an optimizer type', TOAST_WARNING_CONFIG);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: any = {
        type: optimizerForm.type.trim()
      };

      // Add current if provided
      if (optimizerForm.current.trim()) {
        payload.current = parseInt(optimizerForm.current);
      }

      const newOptimizer = await optimizersService.createOptimizer(payload);
      
      // Update Redux state
      dispatch(addOptimizer(newOptimizer));
      
      // Update local state
      setLocalOptimizers(prev => [...prev, newOptimizer]);
      
      // Reset form
      setOptimizerForm({ type: '', current: '' });
      
      toast.success('Optimizer created successfully!', TOAST_CONFIG.toastOptions.success);
    } catch (error) {
      console.error('Error creating optimizer:', error);
      toast.error('Failed to create optimizer. Please try again.', TOAST_CONFIG.toastOptions.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-4">

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Form Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-3">
            Add New Optimizer
          </h3>

          <form onSubmit={handleOptimizerSubmit} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Optimizer Type *
              </label>
              <input
                type="text"
                value={optimizerForm.type}
                onChange={(e) => setOptimizerForm(prev => ({ ...prev, type: e.target.value }))}
                placeholder="e.g., P730-5RM4MRM"
                className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Current (A)
              </label>
              <input
                type="number"
                step="1"
                value={optimizerForm.current}
                onChange={(e) => setOptimizerForm(prev => ({ ...prev, current: e.target.value }))}
                placeholder="e.g., 15"
                className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                min="0"
              />
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting || !optimizerForm.type.trim()}
              className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isSubmitting ? 'Creating...' : 'Create Optimizer'}
            </button>
          </form>
        </div>

        {/* List Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-slate-900">
              Existing Optimizers
            </h3>
            <span className="text-xs text-slate-500">
              {localOptimizers.length} items
            </span>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {localOptimizers.length > 0 ? (
              <div className="flex flex-wrap gap-2 p-2">
                {localOptimizers.map((optimizer) => (
                  <div key={optimizer.id} className="inline-flex items-center px-3 py-1 bg-slate-50 text-slate-800 rounded-full border border-slate-200 hover:bg-slate-100 transition-colors">
                    <span className="text-xs font-medium">{optimizer.type}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-xs text-center py-8">No optimizers found</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};